// These helpers stand in for the Sugar.js prototype extensions this module was
// originally written against (String#titlecase, Date.create, Date#rewind,
// Date#monthsSince, Date#endOfMonth, Date#isBefore/isAfter, Date#format).
// Sugar is not a dependency of this project, so the validators below use these
// instead of relying on patched built-ins.
const titlecase = (field) => (
  String(field)
    .replace(/[_-]+/g, " ")
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
)

// Returns null for anything that is not a parseable date, so callers can treat
// unparseable input as invalid rather than comparing against `Invalid Date`.
const parseDate = (value) => {
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

// Whole calendar months between two dates, not counting a partial final month.
const monthsSince = (laterDate, earlierDate) => {
  let months = (laterDate.getFullYear() - earlierDate.getFullYear()) * 12
  months += laterDate.getMonth() - earlierDate.getMonth()
  if (laterDate.getDate() < earlierDate.getDate()) { months -= 1 }
  return months
}

const subtractYears = (date, years) => {
  const result = new Date(date.getTime())
  result.setFullYear(result.getFullYear() - years)
  return result
}

// `month` is 1-indexed: day 0 of the following month is the last day of this one.
const endOfMonth = (year, month) => new Date(year, month, 0, 23, 59, 59, 999)

export const composeValidators = (...validators) => (
  (value) => validators.reduce((error, validator) => error || validator(value), undefined)
)

export const required = (field) => (value) => {
  const message = field ? `${titlecase(field)} required` : "Required"
  return value ? undefined : message
}

export const requiredBoolean = (field) => (value) => {
  const message = field ? `${titlecase(field)} required` : "Required"
  return value === true || value === false ? undefined : message
}

export const isNotEmpty = (field) => (value) => {
  const message = field ? `${titlecase(field)} required` : "Required"
  return String(value).trim().length === 0 ? message : undefined
}

export const isValidEmail = (value) => {
  // https://stackoverflow.com/questions/15017052/understanding-email-validation-using-javascript
  const re = /^\w+([.+-]\w+)*@\w+([.-]\w+)*(\.\w+)+$/i
  return (re.test(String(value)) || String(value).trim().length === 0) ? undefined : "Please enter a valid email address."
}

export const isValidZip = (value) => {
  const re = /(^\d{5}$)|(^\d{5}-\d{4}$)/
  return (re.test(String(value)) || String(value).trim().length === 0) ? undefined : "Please enter a valid zip code."
}

export const isNotMatchingEmail = (firstEmail, errorMessage) => (value) => {
  const message = errorMessage || "Email already exists in our system."
  return firstEmail.trim() !== value.trim() ? undefined : message
}

export const isDuplicateCoApplicantField = (field, applicants) => (value) => {
  const applicantData = applicants.reduce((acc, applicant) => {
    const fieldValue = applicant && applicant[field]
    if (fieldValue) { acc.push(fieldValue.toLowerCase()) }
    return acc
  }, [])
  if (applicantData.includes(value.toLowerCase())) {
    return `${titlecase(field)} is already associated with this application`
  }
}

export const isValidPassword = (value) => {
  const password = String(value)

  const length = /^.{10,20}$/
  if (!length.test(password)) {
    return "Password must be between 10 and 20 characters long."
  }

  const re = /(?=.*[a-z])(?=.*[0-9])/
  if (!re.test(password)) {
    return "Your password must contain at least one letter and one number."
  }
}

export const isNotMatchingPassword = (password) => (value) => {
  if (!password) {
    return "Please type a password above."
  }
  if (value !== password) {
    return "Please make sure the password matches."
  }
}

export const isValidPhone = (value) => {
  const re = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4}$/im
  return (re.test(String(value).toLowerCase()) || String(value).trim().length === 0) ? undefined : "Please enter a valid phone number."
}

export const dateNotOlderThanMonths = (numberOfMonths, errorMessage) => (value) => {
  const message = errorMessage || "Please use more recent date."
  const comparisonDate = parseDate(value)
  if (!comparisonDate) { return message }
  return monthsSince(new Date(), comparisonDate) > numberOfMonths ? message : undefined
}

export const isNotMatchingListItems = (listItems, errorMessage) => (value) => {
  const message = errorMessage || "Please select an item from the list."
  const noMatch = !listItems.includes(value)
  return noMatch ? message : undefined
}

export const validateStringLength = (minimumLength, errorMessage) => (value) => {
  const message = errorMessage || `Must be ${minimumLength} or more characters`
  return value.length < minimumLength ? message : undefined
}

export const validateAlphaCharacters = ({ allowSpaces }) => (value) => {
  const message = allowSpaces ? "Remove any punctuation" : "Please use only letters"
  const re = allowSpaces ? /[^a-zA-Z ]/ : /[^a-zA-Z]/
  return !re.test(String(value).toLowerCase()) ? undefined : message
}

export const validateFirstAndLastName = (errorMessage) => (value) => {
  const message = errorMessage || "Enter a valid first and last name"
  const arr = value.trim().split(" ") || []
  if (arr.length !== 2) {
    return message
  }
  for (let i = 0; i < arr.length; i += 1) {
    const position = i === 0 ? "First" : "Last"
    const name = arr[i]
    // Tests for invalid characters but keeps hyphens for hyphenated last names
    if (/[^a-zA-Z-]/.test(String(name).toLowerCase())) {
      return "Invalid character"
    }
    if (validateStringLength(2)(name)) {
      return validateStringLength(2, `${position} name must be 2 or more characters`)(name)
    }
  }
  return undefined
}

export const isMatch = (comparison, errorMessage) => (value) => {
  const message = errorMessage || "Does not match records."
  return comparison.toLowerCase().trim() === value.toLowerCase().trim() ? undefined : message
}

export const isValidBirthDate = (value) => {
  const today = new Date()
  const comparisonDate = parseDate(value)
  const oneHundredFiftyYearsAgo = subtractYears(today, 150)
  let valid = true
  if (!comparisonDate || comparisonDate > today || comparisonDate < oneHundredFiftyYearsAgo) {
    valid = false
  }
  return valid ? undefined : "Please enter a valid birth date."
}

export const isEighteenYearsOld = (value) => {
  const birthDate = parseDate(value)
  const eighteenYearsAgo = subtractYears(new Date(), 18)
  const isLegalAdult = Boolean(birthDate) && birthDate < eighteenYearsAgo
  return isLegalAdult ? undefined : "You must be at least 18 years of age."
}

export const isValidTextDate = (value) => {
  const [year, month, day] = String(value).split("-").map((val) => Number(val))
  const currentYear = new Date().getFullYear()
  if (!year || (year < 1900 || year > currentYear)) {
    return "Please enter a valid birth year."
  }
  if (!month || (month < 1 || month > 12)) {
    return "Please enter a valid birth month."
  }
  if (!day || (day < 1 || day > 31)) {
    return "Please enter a valid birth day."
  }
}


export const validateSignatureMatch = (landlordName) => (value) => {
  if (landlordName !== value) {
    return "Please make sure your signature matches the name on your account."
  }
  return null
}

export const validateMultipleEmails = (emails) => {
  // filter for disregarding unnecessary commas
  const emailArray = emails.split(",").map((email) => email.trim()).filter((el) => el)
  const hasError = emailArray.some((email) => isValidEmail(email))
  if (hasError) {
    return "Please enter a valid email address or addresses."
  }
  return null
}

export const isInvalidNumberString = (value) => {
  const numbersOnly = /^[0-9]*$/
  return !numbersOnly.test(String(value))
}

export const validateCreditCardNumber = (value) => {
  // credit cards must be numeric and can have as few as 14 digits and as many as 16 digits
  // https://stripe.com/docs/testing
  // https://developer.intuit.com/app/developer/qbpayments/docs/develop/tutorials/test-your-app#testing-credit-card-transactions
  if (isInvalidNumberString(value)) {
    return "Please enter numeric characters only."
  }
  if (value.length < 14 || value.length > 16) {
    return "Please enter a valid card number."
  }
  return null
}

export const validateCreditCardCVC = (value) => {
  // credit card cvc must be numeric and can have as few as 3 digits and as many as 4 digits
  if (isInvalidNumberString(value)) {
    return "Please enter numeric characters only."
  }
  if (value.length < 3 || value.length > 4) {
    return "Please enter a valid CVC number."
  }
  return null
}

export const validateCreditCardZip = (value) => {
  // zip code must be numeric and 5 digits long
  if (isInvalidNumberString(value)) {
    return "Please enter numeric characters only."
  }
  if (value.length !== 5) {
    return "Please enter a 5 digit zip code."
  }
  return null
}

export const cardExpired = ({ month, year }) => {
  const expiration = endOfMonth(year, month)
  const today = new Date()
  return expiration < today
}

export const validateCreditCardExpiration = (value) => {
  // expiration must be MM/YYYY format
  const [_month, _year] = value.split("/")
  const month = Number(_month)
  let year
  if (_year) { year = Number(_year) }
  if (isInvalidNumberString(value.split("/").join(""))) {
    return "Please enter numeric characters only."
  }

  if (month < 1 || month > 12 || _month.length !== 2) {
    return "Please enter a valid month."
  }

  if (!year || _year.length !== 4) {
    return "Please enter expiration in MM/YYYY format."
  }

  if (cardExpired({ month, year })) {
    return "Card expired."
  }
  return null
}

export const isValidSSN = (value) => {
  const re = /^[0-9]{3}-?[0-9]{2}-?[0-9]{4,5}$/
  if (re.test(String(value).toLowerCase()) || String(value).trim().length === 0) {
    return null
  }
  return "Please enter a valid social security number."
}

export const numericality = (errorMessage) => (value) => {
  const message = errorMessage ? `${errorMessage}` : "Please enter only numbers"
  return Number.isNaN(Number(value)) ? message : null
}

export const maximumLength = (maxLength, errorMessage) => (value) => {
  if (!value) { return null }
  const message = errorMessage || `Must be ${maxLength} or less characters`
  return value.length > maxLength ? message : null
}

export const validateAdminDidNotChangeEmail = (userIsAdmin, savedEmail) => (value) => {
  if (userIsAdmin && savedEmail !== value) {
    return "Admin should not be resetting an email address."
  }
}

export const validateCharacterLength = (limit) => (value) => {
  if (!value) { return null }
  if (value.length > limit) {
    return `Please enter ${limit} characters or less.`
  }
}
